/**
 * designed-to-last.data.ts — Content for the "Designed to Last" chapter
 * (CX-2728).
 *
 * Design-for-recycling principles: color, finish, assembly and monomaterial
 * choices that determine whether a part can be recycled at end of life. Ported
 * from the demo-v784 reference; static today, CMS-driven later (same
 * `ChapterContent` shape). Uses ordered `blocks`: a subheading, a two-column
 * do/don't checklist, the "Reading the grade name" suffix decoder, and the
 * Monomaterial Headlamp annotated-diagram case study.
 *
 * The headlamp exploded-view image is a LOCAL asset (unlike other chapters'
 * mediahub CDN heroes) — the demo ships its own render. Hotspot coordinates and
 * material data mirror demo-v784 HEADLAMP_CALLOUTS / HEADLAMP_PARTS.
 */

import headlampExplodedImg from "../assets/headlamp-exploded.png";
import type { ChapterContent } from "../chapter.types";

const DESIGNED_TO_LAST_HERO_IMAGE =
  "https://mediahub.covestro.com/transform/29627d87-e665-40bf-8130-37c7c0311553/Headlamp";

export const designedToLastChapter: ChapterContent = {
  slug: "designed-to-last",
  title: "Designed to Last",
  eyebrow: "Sustainability Guide",
  heroImage: DESIGNED_TO_LAST_HERO_IMAGE,
  tags: ["Design for Recycling", "Monomaterial", "Finish", "Disassembly"],
  intro:
    "The most sustainable material is one that can actually be recycled at end of life. That depends less on the grade you specify and more on the decisions you make around color, finish, assembly and material mix.\n\nAdd color through direct pigmenting or dyeing of the resin. Spray painting, coatings and film lamination all contaminate the recycling stream. For surface texture, in-mold textures, RHCM and laser etching are the right tools. Soft-touch coatings and laminated films will prevent the part from being recycled cleanly.\n\nLimit material selection to a single resin family wherever possible. Use mechanical fasteners or laser welding — avoid adhesives. PC and PC blends from Covestro cover an exceptionally wide range of properties within a single material family, making monomaterial assemblies feasible across structural, optical, thermal, and decorative functions. These principles are drawn from Covestro's Circular Design Guide, developed in collaboration with REnato lab for the electrical, electronics and home appliance industries.",

  // Full-width alternating bands mirror the Figma section rhythm (node
  // 1659-12288), matching the other chapters. Band A (#fff) holds the best-
  // practices subheading + do/don't columns + suffix decoder; band B (#fafafa)
  // holds the Monomaterial Headlamp annotated-diagram case study.
  blocks: [
    {
      kind: "band",
      surface: "white",
      blocks: [
        {
          kind: "subheading",
          text: "Best design practices for disassembly and recycling",
        },
        {
          kind: "columns",
          left: [
            { kind: "subheading", text: "Compatible Finishing Processes" },
            {
              kind: "checklist",
              items: [
                { label: "Direct pigmenting or dyeing of the resin", ok: true },
                {
                  label: "Inherent metallic effect resin",
                  ok: true,
                  url: "/search?colorEffect=METALLIC_OR_PEARLESCENT",
                },
                {
                  label: "In-mold textures (incl. RHCM)",
                  ok: true,
                  url: "/search?colorTag=1%3Arhcm",
                },
                {
                  label: "Laser marking / laser etching",
                  ok: true,
                  url: "/search?colorTag=1%3Alasermarking",
                },
                { label: "Machining", ok: true },
                { label: "Spray paint / lacquer coatings", ok: false },
                { label: "Film lamination", ok: false },
                { label: "Over-molded soft-touch elastomers", ok: false },
                {
                  label: "Adhesive bonding with dissimilar materials",
                  ok: false,
                },
              ],
            },
          ],
          right: [
            { kind: "subheading", text: "Design for Disassembly" },
            {
              kind: "checklist",
              items: [
                {
                  label:
                    "Limit material selection to a single resin family wherever possible",
                  ok: true,
                },
                {
                  label: "Use part consolidation to reduce component count",
                  ok: true,
                },
                {
                  label:
                    "Use mechanical fasteners or laser welding — avoid adhesives",
                  ok: true,
                  url: "/search?colorTag=2%3Alaserwelding+%28IR-absorbing+part%29",
                },
                {
                  label:
                    "Avoid fasteners made from a different material than the main resin",
                  ok: true,
                },
                {
                  label:
                    "Separate clear materials from opaque to retain their value",
                  ok: true,
                },
                {
                  label:
                    "PC + ABS can be recycled together as PC-ABS — but once blended, cannot be separated again",
                  ok: true,
                },
              ],
            },
          ],
        },
        {
          kind: "suffixDecoder",
          decoder: {
            title: "Reading the grade name",
            text: "The number after the R tells you the recycled content level. R20 = 20% PCR. R30 = 30% PCR. R50 = 50% PCR. R65 = 65% PCR. R75 = 75% PCR. R90 = 90% PCR. The suffix CQ means the grade carries the Circular Intelligence designation — it contains at least 25% alternative, non-fossil raw materials and meets Covestro's threshold for a claimable environmental benefit. RE and RP suffixes indicate bio-circular and chemically recycled feedstocks respectively — see Chapter 2 for those.",
          },
        },
      ],
    },
    {
      kind: "band",
      surface: "gray",
      blocks: [
        {
          kind: "annotatedDiagram",
          title: "Monomaterial Headlamp Concept",
          diagram: {
            image: headlampExplodedImg,
            imageAlt: "Exploded view of a monomaterial PC headlamp assembly",
            introParagraphs: [
              "A conventional automotive headlamp typically combines a wide range of incompatible materials. This concept replaces the entire assembly with PC and PC blends from Covestro — joined with mechanical fasteners and laser welding instead of adhesives, so the assembly can be fully disassembled and recycled.",
            ],
            // Order + numbering follow Figma (1–7); hotspot % from demo HEADLAMP_CALLOUTS.
            parts: [
              {
                number: 1,
                part: "Headlamp cover",
                material: "Makrolon® AL2447 RE/RP",
                keyProperty: "Highest optical and mechanical quality",
                hotspot: { left: 15, top: 72 },
                link: {
                  variant: "view-in-imagio",
                  imagioUrl:
                    "/search?status=ACTIVE&grade=Makrolon%C2%AE+AL2447*R*",
                },
              },
              {
                number: 2,
                part: "Lenses and lightguides",
                material: "Makrolon® LED2245 (HP) RE/RP",
                keyProperty: "High light transmission",
                hotspot: { left: 40, top: 38 },
                link: {
                  variant: "view-in-imagio",
                  imagioUrl:
                    "/search?status=ACTIVE&grade=Makrolon%C2%AE+LED2245*R*",
                },
              },
              {
                number: 3,
                part: "Lenses and functional color parts",
                material: "Makrolon® LED2245 DQ / EL RE/RP",
                keyProperty: "Color-stable optical",
                hotspot: { left: 42, top: 48 },
                link: {
                  variant: "view-in-imagio",
                  imagioUrl:
                    "/search?status=ACTIVE&grade=Makrolon%C2%AE+LED2245*",
                },
              },
              {
                number: 4,
                part: "Reflectors and color stable parts",
                material: "Makrolon® DS801 RE/RP",
                keyProperty: "High-gloss reflective, dimensionally stable",
                hotspot: { left: 60, top: 42 },
                link: {
                  variant: "view-in-imagio",
                  imagioUrl:
                    "/search?status=ACTIVE&grade=Makrolon%C2%AE+DS801*",
                },
              },
              {
                number: 5,
                part: "Heatsinks",
                material: "Makrolon® TC629 RE/RP",
                keyProperty: "Thermally conductive",
                hotspot: { left: 65, top: 14 },
                link: {
                  variant: "view-in-imagio",
                  imagioUrl: "/search?status=ACTIVE&grade=Makrolon%C2%AE+TC629",
                },
              },
              {
                number: 6,
                part: "Bezel and frames",
                material: "Makrolon® 205 R50 / 705 R50",
                keyProperty: "Near to prime mechanics, opaque colors",
                hotspot: { left: 34, top: 52 },
                link: {
                  variant: "view-in-imagio",
                  imagioUrl:
                    "/search?status=ACTIVE&gradeNumber=205&gradeNumber=705&brand=Makrolon&gradeProperty=R50&opacity=OPAQUE&colorGroup=0",
                },
              },
              {
                number: 7,
                part: "Housing",
                material: "Bayblend® T85XF R50",
                keyProperty: "Structural, also foamable",
                hotspot: { left: 55, top: 58 },
                link: {
                  variant: "view-in-imagio",
                  imagioUrl: "/search?grade=BAYBLEND%C2%AE+T85+X+R50",
                },
              },
            ],
          },
        },
      ],
    },
  ],
};
